import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useOptimizedBackgroundUrl } from '../../../shared/hooks/useOptimizedBackgroundUrl';
import { isBase64 } from '../../../shared/util/findAdjustImg';
import {
  FormationFitAlignmentAxis,
  getFormationFitAlignmentAxis,
} from '../../../shared/util/formationUtils';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import defaultImg from '../../../asset/project/default_1.png';
import importIcon from '../../../asset/icon/import.png';
import imageIcon from '../../../asset/icon/image.png';
import {
  FormationImageMode,
  ModalType,
} from '../../../shared/enum/EnumRepository';
import FormationSlotControls from './FormationSlotControls';

export interface FormationSlotImgLayerPanHandlers {
  onPointerDown?: React.PointerEventHandler<HTMLDivElement>;
  onPointerMove?: React.PointerEventHandler<HTMLDivElement>;
  onPointerUp?: React.PointerEventHandler<HTMLDivElement>;
  onPointerCancel?: React.PointerEventHandler<HTMLDivElement>;
}

export interface FormationSlotImgLayerProps {
  peId: string;
  slotIndex: number;
  backgroundImg: string;
  imageWidth: number;
  imageHeight: number;
  imageMode: FormationImageMode;
  positionX: number;
  positionY: number;
  isEditMode: boolean;
  isEmpty: boolean;
  canPan: boolean;
  isDragging: boolean;
  handleChange?: (originUrl: string, width: number, height: number, fileName: string) => void;
  handleImageModeChange: (imageMode: FormationImageMode) => void;
  panHandlers?: FormationSlotImgLayerPanHandlers;
  'aria-label'?: string;
}

const FormationSlotImgLayer: React.FC<FormationSlotImgLayerProps> = ({
  peId,
  slotIndex,
  backgroundImg,
  imageWidth,
  imageHeight,
  imageMode,
  positionX,
  positionY,
  isEditMode,
  isEmpty,
  canPan,
  isDragging,
  handleChange,
  handleImageModeChange,
  panHandlers,
  'aria-label': ariaLabel,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const slotImageRef = useRef<HTMLDivElement | null>(null);
  const [fitAlignmentAxis, setFitAlignmentAxis] = useState<FormationFitAlignmentAxis>('none');
  const { setStandardModal } = useModalStore();
  const hasImage = backgroundImg !== '';
  const isViewingDefaultImage = !isEditMode && !hasImage;
  const displayImageMode = isViewingDefaultImage ? FormationImageMode.FILL : imageMode;
  const displayPositionX = isViewingDefaultImage ? 50 : positionX;
  const displayPositionY = isViewingDefaultImage ? 50 : positionY;
  const isLocalImage = isBase64(backgroundImg);
  const { displayUrl, containerRef: optimizeContainerRef } = useOptimizedBackgroundUrl(
    backgroundImg,
    hasImage && !isLocalImage,
  );
  const setSlotImageRef = useCallback((node: HTMLDivElement | null) => {
    slotImageRef.current = node;
    optimizeContainerRef(node);
  }, [optimizeContainerRef]);

  useEffect(() => {
    const element = slotImageRef.current;
    if (!element) return;

    const updateAlignmentAxis = (width: number, height: number) => {
      const nextAxis = getFormationFitAlignmentAxis(
        width,
        height,
        imageWidth,
        imageHeight,
      );
      setFitAlignmentAxis((currentAxis) => (
        currentAxis === nextAxis ? currentAxis : nextAxis
      ));
    };

    const initialRect = element.getBoundingClientRect();
    updateAlignmentAxis(initialRect.width, initialRect.height);

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(([entry]) => {
      updateAlignmentAxis(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [imageHeight, imageWidth]);

  const foregroundUrl = (() => {
    if (!hasImage) return defaultImg;
    if (isLocalImage) return backgroundImg;
    // meta 준비 전(null)에는 원본 대신 default — OptimizedImg와 동일
    return displayUrl ?? defaultImg;
  })();

  const importWorkOrDetail = () => {
    setStandardModal({
      modalType: ModalType.IMPORT_STATION_FOR_FORMATION_SLOT,
      title: peId,
      value: String(slotIndex),
      handleChange: () => {},
    });
  };

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !handleChange) return;

    const reader = new FileReader();
    reader.onload = () => {
      const localImageUrl = reader.result as string;
      const img = new Image();
      img.onload = () => {
        handleChange(localImageUrl, img.width, img.height, file.name);
      };
      img.src = localImageUrl;
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const triggerImageFileInput = () => {
    fileInputRef.current?.click();
  };
  const hasActivePanAxis = canPan && fitAlignmentAxis !== 'none';

  return (
    <SlotImageDiv
      ref={setSlotImageRef}
      $foregroundUrl={foregroundUrl}
      $imageMode={displayImageMode}
      $positionX={displayPositionX}
      $positionY={displayPositionY}
      $isEditMode={isEditMode}
      $isEmpty={isEmpty}
      $canPan={hasActivePanAxis}
      $isDragging={isDragging}
      $fitAlignmentAxis={fitAlignmentAxis}
      aria-label={ariaLabel}
      role={hasImage ? 'img' : undefined}
      {...panHandlers}
    >
      {isEditMode && (
        <>
          <BtnContainer onPointerDown={(event) => event.stopPropagation()}>
            <UniversalIconBtn
              size="fit"
              border={true}
              backgroundImg={importIcon}
              onClick={importWorkOrDetail}
            />
            <UniversalIconBtn
              size="fit"
              border={true}
              backgroundImg={imageIcon}
              onClick={triggerImageFileInput}
            />
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageFileChange}
            />
          </BtnContainer>
          {!isEmpty && (
            <FormationSlotControls
              imageMode={imageMode}
              onImageModeChange={handleImageModeChange}
            />
          )}
        </>
      )}
    </SlotImageDiv>
  );
};

const BtnContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: min(0.5vw, 0.5vh);
  z-index: 1;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const SlotImageDiv = styled.div<{
  $foregroundUrl: string;
  $imageMode: FormationImageMode;
  $positionX: number;
  $positionY: number;
  $isEditMode: boolean;
  $isEmpty: boolean;
  $canPan: boolean;
  $isDragging: boolean;
  $fitAlignmentAxis: FormationFitAlignmentAxis;
}>`
  position: absolute;
  inset: 0;
  background-color: transparent;
  background-image: ${({ $foregroundUrl }) =>
    $foregroundUrl ? `url(${$foregroundUrl})` : 'none'};
  background-size: ${({ $imageMode }) => (
    $imageMode === FormationImageMode.FIT ? 'contain' : 'cover'
  )};
  background-position: ${({ $positionX, $positionY }) => `${$positionX}% ${$positionY}%`};
  background-repeat: no-repeat;
  touch-action: ${({ $canPan, $imageMode, $fitAlignmentAxis }) => {
    if (!$canPan) return 'auto';
    if ($imageMode === FormationImageMode.FILL) return 'none';
    return $fitAlignmentAxis === 'horizontal' ? 'pan-y' : 'pan-x';
  }};
  cursor: ${({ $canPan, $isDragging }) => {
    if (!$canPan) return 'default';
    return $isDragging ? 'grabbing' : 'grab';
  }};
  user-select: none;
  border: ${({ $isEditMode, $isEmpty, theme }) =>
    $isEditMode && $isEmpty ? `1px dashed ${theme.colors.color_Gray_05}` : 'none'};
`;

export default FormationSlotImgLayer;
